import React, {Fragment, useEffect, useState} from "react";
import __ from "lodash";
import moment from "moment/moment";
import axios from "axios";
import {FormControl, FormGroup, Grid} from "@material-ui/core";
import {FormDialog, LabelledPaper, Text} from "../../../controls";
import StringToJSX from "../../../functions/StringToJsx";
import {populateComplexTemplate} from "../../../functions/PopulateTemplate";
import {getApiSitePrefix} from "../../../functions/SiteDefaults";
import {getTemplate} from "../../../functions/TemplateHelperFunctions";

const apiSitePrefix = getApiSitePrefix();
const defaultLabelledPaperProps = {
  checkChildren: true,
  style: {display: "block", border: "solid 1px #b9b9b9" },
  labelStyle: {background: "#f0f0f0"},
  collapsible: false,
}

const EmailBodyView = (props) => {
  const record = props.record;

  return (
    <div style={{width: "100%", maxHeight: props.maxHeight, overflowY: "scroll"}}>
      <StringToJSX domString={populateComplexTemplate(record.template, record, [...record.repeatData])}/>
    </div>
  );
}

const ViewQuoteDialog = (props) => {
  const {SystemData, extraKeys, handleUpdate, classes, defaultValues} = props;
  const eventBus = SystemData.eventBus;
  const ownerDepartment = defaultValues?.ownerDepartment || 'COMMERCIAL';

  const [dialogStates, setDialogStates] = useState({
    showDialog: false,
    record: {},
    title: 'View Quote'
  });

  const formId = 'ViewQuote';

  const getCargoData = async (enquiryUuid) => {
    try {
      const response = await axios.get(`${apiSitePrefix}/enquirycargo/list/${enquiryUuid}`);
      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        return response.data;
      }

      if (!response.data) {
        const message = `Could not retrieve Enquiry Cargo. Server is not responding`
        console.log(message);
        SystemData.alert("Error", message);
      } else if (!response.data.success) {
        const message = `Could not retrieve Enquiry Cargo. Server returned ${response.data.error}`;
        console.log(message);
        SystemData.alert("Error", message);
      }

      return null;
    } catch (err) {
      const message = `Could not retrieve Enquiry Cargo. Server is not responding`;
      console.log(message);
      SystemData.alert("Error", message);
    }
  }

  const getQuoteTerms = async (terms, quoteType) => {
    let quoteTerms = [];
    let message = "";
    const termsDescription = terms === "QUOTE EXCLUSIONS" ? "quote exclusions" : "subject to items";
    const termProperty = terms === "QUOTE EXCLUSIONS" ? "excluded" : "subjectTo";
    try {
      let response = await axios.get(`${apiSitePrefix}/combovalues/list/${terms}/${quoteType}`);
      if (response && response.data && Array.isArray(response.data)) {
        response.data.forEach(item => {
          const term = {};
          term[termProperty] = item.value;
          quoteTerms.push(term);
        });
      }
      if (!response.data) {
        message = `Could not retrieve ${termsDescription}. Server is not responding`;
        console.log(message);
        SystemData.alert("Error", message);
      }
    } catch (err) {
      message = `Could not retrieve ${termsDescription}. Server is not responding`
      console.log(message);
      SystemData.alert("Error", message);
    }

    return quoteTerms
  }

  const getDefaultValues = async () => {
    try {
      const response = await axios.get(`${apiSitePrefix}/enquiries/defaults`);
      if (response.data && response.data.success) {
        return response.data.defaultValues;
      }

      if (!response.data) {
        console.log(`Could not retrieve Enquiries Defaults. Server is not responding`);
        SystemData.alert("Error", `Could not retrieve Enquiries Defaults. Server is not responding`);
      } else if (!response.data.success) {
        console.log(`Could not retrieve Enquiries Defaults. Server returned ${response.data.error}`);
        SystemData.alert("Error", `Could not retrieve Enquiries Defaults. Server returned ${response.data.error}`);
      }
    } catch (err) {
      console.log(`Could not retrieve Enquiries Defaults. Server is not responding`);
      SystemData.alert("Error", `Could not retrieve Enquiries Defaults. Server is not responding`);
    }
  }

  useEffect(()=> {
    const open = async (record) => {
      const {quoteReference} = record;
      const title = `View Quote Reference ${quoteReference}`;
      const defaultValues = await getDefaultValues();
      const templateUuid = __.has(defaultValues, record.quoteTemplate) ? defaultValues[record.quoteTemplate] : "";
      const enquiryCargo = await getCargoData(record.associatedEnquiryUuid);
      const template = await getTemplate(SystemData, "emailmanager", "template", "SALES QUOTE", templateUuid);
      const departmentUsers = record.departmentUsers;
      let commercialManager = "";

      if (!templateUuid || !template || template === "")
        return;

      if (departmentUsers && departmentUsers.data && Array.isArray(departmentUsers.data)) {
        const user = __.find(departmentUsers.data, {user_id: record.commercialOwnerId});
        if (user)
          commercialManager = `${user.firstName} ${user.lastName}`;
      }

      let exclusions = [];
      let subjectTo = [];
      if (record.payloadType) {
        exclusions = await getQuoteTerms("QUOTE EXCLUSIONS", record.payloadType);
        subjectTo = await getQuoteTerms("QUOTE SUBJECT TO", record.payloadType);
      }

      record["templateType"] = "SALES QUOTE";
      record["enquiryDate"] = moment(record.quoteDate).format("DD/MMM");
      record["totalRevenue"] = record.totalRevenue !== "" ? new Intl.NumberFormat('en-US', { maximumSignificantDigits: 3 }).format(record.totalRevenue) : "" ;
      record["repeatData"] = enquiryCargo ? [enquiryCargo, exclusions, subjectTo] : [[], exclusions, subjectTo];
      record["commercialManager"] =  commercialManager;
      record["template"] = template;

      setDialogStates(
        {
          ...dialogStates,
          showDialog:true,
          title,
          record,
        }
      );
    }

    const close = () => {
      setDialogStates({...dialogStates, showDialog:false, record: {}, title: 'View Quote'});
    }

    eventBus.bind("ViewQuoteDialog.open", open);
    eventBus.bind("ViewQuoteDialog.close", close);

    return () => {
      eventBus.unbind("ViewQuoteDialog.open", open);
      eventBus.unbind("ViewQuoteDialog.close", close);
    }
  }, [dialogStates, eventBus, SystemData]);


  if (dialogStates.showDialog) {
    return (
      <>
        <FormDialog
          formId={formId}
          title={dialogStates.title}
          message=""
          open={dialogStates.showDialog}
          record={dialogStates.record}
          dialogOptions={{maxWidth: "lg", fullWidth: true}}
          classes={classes}
          movable
          buttons = {{
            cancel:{icon: "cancel", key: "Close", label: "Close", color: "primary"}
          }}
          onChange={(state) => {
            setDialogStates({...dialogStates, showDialog: false, record: null});
          }}

        >
          <Grid container checkChildren={true} spacing={3}>

            <Grid item xs={12} checkChildren={true}>
              {(record) => (
                <LabelledPaper {...defaultLabelledPaperProps} label={`Quote`} >
                  <FormControl checkChildren={true} key="FC-genericemailbody" style={{display: "block" }}>
                    <FormGroup checkChildren={true} key="FG-genericemailbody" style={{padding: "8px 8px 8px 8px"}}>
                      {record.template && record.template !== ""
                        ? <EmailBodyView record={record}/>
                        : <span>Missing Quote Template</span>
                      }
                    </FormGroup>
                  </FormControl>
                </LabelledPaper>
              )}
            </Grid>
          </Grid>
        </FormDialog>
      </>
    )
  }
  return (<></>);

}

export default ViewQuoteDialog;
